import { isDemoMode, render, type AccountInfo } from '@sl/core';
import { Button } from '../../ui/Button/Button';
import { Icon, type IconName } from '../../ui/Icon';
import { testId } from '../testId';
import { SHELL_COPY } from './copy';
import { useShell } from './shellContext';
import type { ShellLive } from './useShellLive';
import styles from './AppShell.module.css';

// Freshness + refresh — REQ-283. The label says how old the numbers are and the control asks for newer ones.
//
// REQ-283 AC5 is the rule that shapes this file: on a DEMO account there is nothing to fetch, so the label
// reads "Sample data" — never "Updated n min ago", never amber — while the refresh control stays present and
// always available (it re-runs the demo server's evaluation). `AccountInfo.limits.refreshMinutes` is 0 on a
// demo, so no wait is ever shown there either.
//
// The live label comes from the sync status of T-021 / T-023: `queries.syncStatus(id)` polls itself while a
// sync is running, so this component only observes it.
//
// **T-210 — the label is `@sl/core/queries`' now** (`liveFreshness`, T-208 / D-1078), which is where the
// derivation belongs: it reads one instant chain (§2.3's `dataAsOf` → the ANSWER's own `meta.dataAsOf` →
// §12.2's, and never `meta.generatedAt`) and hands back the tone, REQ-283 AC2's amber words and its
// three-hour banner beside the sentence.
//
// **T-211 — the three levels of `mockups/web/w17-states.html` §6, and nothing invented to draw them.**
// The label is one `<span>` with a `data-tone`, and which of the four tones it carries is read off core's
// typed state in this order:
//
//   `saved`  the device is offline — `offline.message` is `state.offline_since`, which states the clock time
//            of what is on screen (w17 level 3).
//   `stale`  REQ-283 AC2's amber, past sixty minutes: the sentence, then `staleLabel` after a middot
//            ("Updated 1 h 12 min ago · may be out of date"). Amber is never the only cue — the glyph
//            changes with it (`alert-triangle`), which is w17 §6 rule 2 and REQ-006 AC3's mechanism.
//   `sample` a demo: "Sample data", never an age and never amber (REQ-283 AC5).
//   `fresh`  everything else.
//
// **The rate limit is NOT one of them, and that is a decision rather than an omission.** w17 §5 draws the
// next-update instant twice — in the caption and in the band under it — and the catalogue has exactly ONE
// sentence for it (`state.rate_limited`, "Limited by Meta — next update at 2:50 pm"), so drawing it in both
// places prints the same sentence twice on one screen. The band is where it goes, for a reason a reader can
// check: the caption is hidden below 1400 px (DESIGN_SYSTEM §10's give-way order) and the band is on every
// width, and REQ-286 AC2 asks for that time to reach the owner, not to reach a wide screen. So the caption
// keeps the question it exists to answer — how old are these numbers — and the band answers why they are not
// newer. The instant still comes from §2.3 or §1.6 and structurally never from §12.2 (D-1065, D-1078 (d));
// `AppShell.tsx` hands it to the band.
//
// **The first sync has no label at all.** Before anything has been read there is no age to state, and
// `state.first_sync` is 107 characters — measured at 578 px, which starved the account name to "R…" on the
// first screen of a live run (T-210's floor, BUG-089's trap). The sentence belongs to a banner that has room
// for it (REQ-002 AC2, w17 §5), so the caption steps aside and the control stays: BUG-089's order said the
// label yields and the control never does, and this is that rule taken to its end.
//
// **`freshnessMessage` IS DELETED HERE (T-214), with the phone's copy and with the `DECLARED_TWINS` entry
// that named the pair** — one change, because the guard's scan
// (`packages/core/src/boundary.guard.test.ts`) finds a twin by its printed BODY and an entry whose twin has
// stopped being one fails as STALE: removing either side alone reddens it, and so does leaving a one-line
// re-export in its place, which has no body to find (T-210 measured both — D-1080). The derivation it held
// is `@sl/core/queries`' `freshnessMessage` / `liveFreshness`, lifted verbatim by T-208, and this component
// has read the label off `liveState` since T-210; nothing in the product called this copy. The proof that
// core answers identically on all four branches was `t210.liveState.test.tsx`'s, and with one of the two
// functions gone there is nothing left to compare — so that file now pins the four SENTENCES against core,
// which is the half of it that still says something.

/** The four tones of w17 §6, as a `data-tone` the CSS and a test can both read without looking at a colour. */
export type FreshnessLabelTone = 'sample' | 'fresh' | 'stale' | 'saved';

/** Which glyph goes with which tone — never colour alone (w17 §6 rule 2, DESIGN_SYSTEM §4.1). */
const TONE_ICON: Record<FreshnessLabelTone, IconName> = {
  sample: 'clock',
  fresh: 'clock',
  stale: 'alert-triangle',
  saved: 'cloud-off',
};

export interface FreshnessProps {
  account: AccountInfo;
  /** The shell's ONE read of the answer (`useShellLive`), so the label and the banners cannot disagree. */
  live: ShellLive;
}

export function Freshness({ account, live }: FreshnessProps) {
  const { messages } = useShell();
  const { state, pulled, requesting, refresh } = live;
  const demo = isDemoMode(account.mode);

  // The sentence, in the order w17 §6 reads it (see the header): a saved copy beats how old this is.
  const offline = state.offline.message;
  const label = offline ?? state.freshness.message;
  // The tone follows the sentence. `saved` is w17 level 3 — we are showing a copy and saying its time.
  const tone: FreshnessLabelTone = offline !== null ? 'saved' : state.freshness.tone;
  const text = render(label, messages);
  // REQ-283 AC2's own words, beside the sentence rather than inside it, because the sentence is the
  // catalogue's and this is the catalogue's too (`state.may_be_out_of_date`) — two keys, one line.
  // Only on the age tone: a saved copy already says why it is a saved copy.
  const staleLabel = tone === 'stale' ? state.freshness.staleLabel : null;
  const staleWords = staleLabel === null ? null : render(staleLabel, messages);
  const sentence = staleWords === null ? text : `${text} · ${staleWords}`;

  // T-283 — the live look (`?dataset=live`, T-282): a real account whose numbers come from the pull, on a build
  // with no transport to Meta at all. The label is right already (`dataAsOf` is the pull's instant, and
  // `sampleData` is false, so it reads "Updated 2 h ago"); what was wrong were the two sentences about the
  // CONTROL, which promised newer numbers from a press that fetches nothing.
  //
  // **T-287: the answer itself says where it came from** (`meta.source === 'pull'`, 0.3.8). T-283 had to read
  // the app's own `?dataset=` because no response said so, which is a guess about the server dressed as a
  // fact — and the wrong guess the day a backend serves a pulled file, or an `http` build is pointed at one.
  // The config is no longer consulted here at all: a response that has not arrived yet promises nothing
  // special, which is the safe way round (the hint is a title, not a figure).
  const hint = demo
    ? SHELL_COPY.refreshHintDemo
    : pulled
      ? SHELL_COPY.refreshHintPull
      : SHELL_COPY.refreshHintLive;

  // REQ-283 AC1's second half — "says when the next refresh is possible" (REQ-286 AC1's five minutes). A demo
  // never waits (`nextManualRefreshAt` is always null there, API §2.4) and core says so even where a fixture
  // forgets, so this branch cannot reach a sample account.
  const wait = state.refresh.wait === null ? null : render(state.refresh.wait, messages);
  const refusing = !state.refresh.available && wait !== null;
  const syncing = requesting || state.refresh.running;

  return (
    <div className={styles.freshness}>
      {/* REQ-002 AC2: before the first read there is no age, and the 107-character sentence has a banner of
          its own (`LiveStates`) — so the caption steps aside rather than truncating to "Loading your acc…"
          and starving the account name beside it (T-210's measurement). */}
      {state.firstSync === null ? (
        <span
          className={styles.freshnessLabel}
          data-demo={demo || undefined}
          data-tone={tone}
          data-testid={testId('shell', 'freshness')}
        >
          <Icon name={TONE_ICON[tone]} size={14} />
          {/* The sentence is its own box so it can truncate: `text-overflow` does not apply to the inline-flex
            label around it (BUG-089's note). The whole sentence stays in the DOM — assistive technology reads
            all of it — and the `title` is for the pointer user, who is the only one an ellipsis costs
            anything. */}
          <span className={styles.freshnessText} title={sentence}>
            {text}
          </span>
          {/* **The amber words never give way, and the age does.** BUG-089 settled the ORDER in which the top
            bar's left group yields (the account name, then the caption's text, never the control); this is
            that order one level in, because the two halves of an amber caption are not worth the same. "may
            be out of date" is the whole of REQ-283 AC2; "Updated 3 h ago" is REQ-283 AC1's, and it is also in
            the `title` and — past three hours — in the banner below, so it is the half that can afford to go.
            The separator travels WITH the clause it introduces (design QA 6 §3.4), so it leaves with the age
            rather than dangling in front of the warning. `AppShell.module.css` has the measurement. */}
          {staleWords === null ? null : (
            <>
              <span className={styles.freshnessSep} aria-hidden="true">
                {' · '}
              </span>
              <span className={styles.freshnessStale}>{staleWords}</span>
            </>
          )}
        </span>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        icon="refresh"
        loading={syncing}
        /* w17 §6's "Refresh — available at 9:21 am": the control keeps its NAME and gains the reason, so a
           screen reader on a refused button is told why rather than left with a dead verb. Two existing
           strings and a dash; neither is reworded. */
        aria-label={refusing ? `${SHELL_COPY.refresh} — ${wait}` : SHELL_COPY.refresh}
        title={refusing ? wait! : hint}
        /* `aria-disabled`, not `disabled` — the same choice T-034 made for `loading` and for the same reason:
           the limit arrives from the SERVER while the hand is still on the control (press → the pass runs →
           §2.3 starts reporting `nextManualRefreshAt`), and a real `disabled` at that moment drops focus to
           `<body>`. The press is refused here instead, so the control keeps its place in the tab order and
           its reason (REQ-281). w17 draws both spellings; this is the one that costs nobody their place. */
        aria-disabled={refusing || syncing ? true : undefined}
        data-refusing={refusing || undefined}
        testID={testId('shell', 'refresh')}
        onClick={refusing ? undefined : refresh}
      />
    </div>
  );
}
